import AxiosClient from './client';
import axios from 'axios';
import { API_BASE_URL } from '@/config/constants';

export interface ImportStandardsResponse {
  success: boolean;
  message?: string;
  data?: {
    imported_count: number;
    total_rows: number;
  };
  errors?: string[];
}

export const standardsAPI = {
  /**
   * Download Excel template for standards import
   */
  downloadTemplate: async (propertyTypeId: number): Promise<void> => {
    try {
      // Get token from storage
      const token =
        sessionStorage.getItem('access_token') || localStorage.getItem('token');

      const response = await axios.get(
        `${API_BASE_URL}/property-types/${propertyTypeId}/standards/template`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `standards_template_${propertyTypeId}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || 'Failed to download template'
        );
      }
      throw new Error('Failed to download template');
    }
  },

  /**
   * Import standards from Excel/CSV file
   */
  importExcel: async (
    propertyTypeId: number,
    file: File
  ): Promise<ImportStandardsResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const client = new AxiosClient<FormData, ImportStandardsResponse>({
        endpoint: `/property-types/${propertyTypeId}/standards/import`,
        contentType: 'multipart/form-data',
        isToken: true,
      });

      const response = await client.post(formData);
      return response.data as unknown as ImportStandardsResponse;
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  },

  /**
   * Import standards from CSV file (specific CSV format)
   */
  importCSV: async (
    propertyTypeId: number,
    file: File
  ): Promise<ImportStandardsResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const client = new AxiosClient<FormData, ImportStandardsResponse>({
        endpoint: `/property-types/${propertyTypeId}/standards/import-csv`,
        contentType: 'multipart/form-data',
        isToken: true,
      });

      const response = await client.post(formData);
      return response.data as unknown as ImportStandardsResponse;
    } catch (error) {
      console.error('CSV import error:', error);
      throw error;
    }
  },
};
